<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import PostDataService from '@/services/PostDataService'
import PostCategoryDataService, { type PostCategory } from '@/services/PostCategoryDataService'
import RichTextEditor from '@/components/RichTextEditor.vue'
import type Post from '@/types/Post'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import axiosClient from '@/axios'

const route = useRoute()
const router = useRouter()

// Reactive state
const currentPost = ref<Post>({} as Post)
const originalPost = ref<Post>({} as Post) // Track original state for comparison
const postCategories = ref<PostCategory[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const isDeleteDialogOpen = ref(false)
const isUpdating = ref(false)
const imagePreviewUrl = ref<string | null>(null)
const hasUnsavedChanges = ref(false)
const isLeaveWarningOpen = ref(false)
const pendingRoute = ref<{ name?: string; path?: string } | null>(null)

// Watch for changes to detect unsaved changes
watch([
  () => currentPost.value.title,
  () => currentPost.value.slug,
  () => currentPost.value.content,
  () => currentPost.value.category_id,
  () => imagePreviewUrl.value
], () => {
  if (originalPost.value.id) {
    hasUnsavedChanges.value =
      currentPost.value.title !== originalPost.value.title ||
      currentPost.value.slug !== originalPost.value.slug ||
      currentPost.value.content !== originalPost.value.content ||
      currentPost.value.category_id !== originalPost.value.category_id ||
      imagePreviewUrl.value !== null
  }
}, { deep: true })

// Generate slug from title
const generateSlug = (title: string): string => {
  // Convert Slovak characters to ASCII equivalents
  const slug = title
    .replace(/[áä]/g, 'a')
    .replace(/[č]/g, 'c')
    .replace(/[ď]/g, 'd')
    .replace(/[é]/g, 'e')
    .replace(/[í]/g, 'i')
    .replace(/[ĺľ]/g, 'l')
    .replace(/[ň]/g, 'n')
    .replace(/[óô]/g, 'o')
    .replace(/[ŕ]/g, 'r')
    .replace(/[š]/g, 's')
    .replace(/[ť]/g, 't')
    .replace(/[ú]/g, 'u')
    .replace(/[ý]/g, 'y')
    .replace(/[ž]/g, 'z')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return slug
}

// Auto-generate slug from title when title changes
watch(() => currentPost.value.title, (newTitle) => {
  // Only auto-generate if slug is empty or if this is a new post
  if (newTitle && (!currentPost.value.slug || !currentPost.value.id)) {
    currentPost.value.slug = generateSlug(newTitle)
  }
})

// Route guard to warn about unsaved changes
onBeforeRouteLeave((to, from, next) => {
  if (hasUnsavedChanges.value) {
    pendingRoute.value = { name: to.name as string, path: to.path }
    isLeaveWarningOpen.value = true
    next(false) // Prevent navigation initially
  } else {
    next()
  }
})

// Browser beforeunload event
const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  if (hasUnsavedChanges.value) {
    e.preventDefault()
    e.returnValue = 'Máte neuložené zmeny. Naozaj chcete opustiť túto stránku?'
    return e.returnValue
  }
}

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

// Computed properties
const isFormValid = computed(() => {
  return currentPost.value.title &&
    currentPost.value.title.trim().length > 0 &&
    currentPost.value.content &&
    currentPost.value.content.trim().length > 0
})

// Computed property for image display URL (existing image from server or preview)
const displayImageUrl = computed(() => {
  if (imagePreviewUrl.value) {
    return imagePreviewUrl.value
  }
  if (currentPost.value.image) {
    // Extract base URL from axios configuration and construct image URL
    // Remove '/api/v1/' from the end to get the base domain
    const baseURL = axiosClient.defaults.baseURL || 'https://api.bizinix.sk/api/v1/'
    const apiBaseUrl = baseURL.replace(/\/api\/v1\/?$/, '')
    return `${apiBaseUrl}/storage/post_images/${currentPost.value.image}`
  }
  return null
})

const selectedCategory = computed(() => {
  return postCategories.value.find(cat => cat.id === currentPost.value.category_id)
})

const formattedCreatedDate = computed(() => {
  if (!currentPost.value.created_at) return ''
  return new Date(currentPost.value.created_at).toLocaleDateString('sk-SK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
})

const formattedUpdatedDate = computed(() => {
  if (!currentPost.value.updated_at) return ''
  return new Date(currentPost.value.updated_at).toLocaleDateString('sk-SK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
})

// Methods for handling unsaved changes warning
const confirmLeave = () => {
  hasUnsavedChanges.value = false
  isLeaveWarningOpen.value = false
  if (pendingRoute.value) {
    if (pendingRoute.value.name) {
      router.push({ name: pendingRoute.value.name })
    } else if (pendingRoute.value.path) {
      router.push(pendingRoute.value.path)
    }
  }
}

const cancelLeave = () => {
  isLeaveWarningOpen.value = false
  pendingRoute.value = null
}

// Methods
const fetchPostCategories = async () => {
  try {
    const response = await PostCategoryDataService.getAll()
    // Handle nested response structure: response.data.data
    postCategories.value = (response.data as { data?: PostCategory[] })?.data || response.data
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : 'Neznáma chyba'
    console.error('Error fetching post categories:', e)
    toast.error('Chyba pri načítavaní kategórií: ' + errorMessage)
  }
}

const fetchPost = async (id: string | string[]) => {
  if (!id) return
  loading.value = true
  error.value = null
  try {
    const response = await PostDataService.getPostById(Array.isArray(id) ? id[0] : id)
    // Handle nested response structure: response.data.data
    currentPost.value = response.data.data || response.data
    // Store original post state for comparison
    originalPost.value = JSON.parse(JSON.stringify(currentPost.value))
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : 'Neznáma chyba'
    error.value = errorMessage
    toast.error('Chyba pri načítavaní článku: ' + errorMessage)
  } finally {
    loading.value = false
  }
}

const updatePost = async () => {
  if (!currentPost.value.id) return

  isUpdating.value = true

  try {
    // Debug: Log the data being sent to the API
    console.log('Updating post with data:', {
      id: currentPost.value.id,
      title: currentPost.value.title,
      content: currentPost.value.content,
      image: currentPost.value.image,
      category_id: currentPost.value.category_id
    })

    await PostDataService.update(currentPost.value.id, currentPost.value)
    hasUnsavedChanges.value = false // Reset unsaved changes flag after successful save
    toast.success('Článok bol úspešne aktualizovaný!')
    router.push({ name: 'Zoznam článkov' })
  } catch (e: unknown) {
    console.error('Update error:', e)
    const errorMessage = e instanceof Error ? e.message : 'Neznáma chyba'
    toast.error('Chyba pri aktualizácii článku: ' + errorMessage)
  } finally {
    isUpdating.value = false
  }
}

const deletePost = async () => {
  if (!currentPost.value.id) return

  try {
    await PostDataService.delete(currentPost.value.id)
    toast.success('Článok bol úspešne zmazaný!')
    router.push({ name: 'Zoznam článkov' })
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : 'Neznáma chyba'
    toast.error('Chyba pri mazaní článku: ' + errorMessage)
  } finally {
    isDeleteDialogOpen.value = false
  }
}

const updateImage = async (evt: Event) => {
  const target = evt.target as HTMLInputElement
  if (!target.files || !target.files[0] || !currentPost.value.id) return

  const file = target.files[0]

  // Create preview URL for the selected image
  if (imagePreviewUrl.value) {
    URL.revokeObjectURL(imagePreviewUrl.value)
  }
  imagePreviewUrl.value = URL.createObjectURL(file)

  try {
    const response = await PostDataService.uploadImage(currentPost.value.id, file)

    // Update the post image with the filename returned from backend
    if (response.data && response.data.status === 'success') {
      currentPost.value.image = file.name
      toast.success('Obrázok úspešne nahraný!')

      // Clear the preview URL since we now have the server image
      if (imagePreviewUrl.value) {
        URL.revokeObjectURL(imagePreviewUrl.value)
        imagePreviewUrl.value = null
      }
    } else {
      throw new Error(response.data?.message || 'Upload failed')
    }
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : 'Neznáma chyba'
    toast.error('Chyba pri nahrávaní obrázka: ' + errorMessage)

    // Reset preview on error
    if (imagePreviewUrl.value) {
      URL.revokeObjectURL(imagePreviewUrl.value)
      imagePreviewUrl.value = null
    }

    // Reset the post image
    currentPost.value.image = ''
  }
}

const removeImage = () => {
  // Clear the preview URL
  if (imagePreviewUrl.value) {
    URL.revokeObjectURL(imagePreviewUrl.value)
    imagePreviewUrl.value = null
  }

  // Clear the current post image
  currentPost.value.image = ''

  // Reset the file input
  const fileInput = document.getElementById('image') as HTMLInputElement
  if (fileInput) {
    fileInput.value = ''
  }

  toast.info('Obrázok bol odstránený. Nezabudnite uložiť zmeny.')
}

const handleImageError = () => {
  // Clear preview URL if it exists (this was a preview image that failed)
  if (imagePreviewUrl.value) {
    URL.revokeObjectURL(imagePreviewUrl.value)
    imagePreviewUrl.value = null
  }

  // Show error toast for server image loading failure
  if (currentPost.value.image && !imagePreviewUrl.value) {
    toast.error('Nepodarilo sa načítať obrázok z servera')
  }
}

const goBack = () => {
  if (hasUnsavedChanges.value) {
    pendingRoute.value = { name: 'Zoznam článkov' }
    isLeaveWarningOpen.value = true
  } else {
    router.go(-1)
  }
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchPost(route.params.id),
    fetchPostCategories()
  ])
})

// Cleanup preview URL on unmount
onUnmounted(() => {
  if (imagePreviewUrl.value) {
    URL.revokeObjectURL(imagePreviewUrl.value)
  }
})
</script>

<template>
  <main class="min-h-screen bg-gray-50">
    <!-- Enhanced Header with gradient background -->
    <div class="bg-gradient-to-r from-teal-600 to-cyan-600 shadow-lg">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <!-- Left section with back button -->
          <div class="flex items-center space-x-4 mb-4 sm:mb-0">
            <button @click="goBack" type="button"
              class="inline-flex items-center px-4 py-2 border border-white border-opacity-20 rounded-lg shadow-sm text-sm font-medium text-white bg-white bg-opacity-10 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 backdrop-blur-sm transition-all duration-200">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                stroke="currentColor" class="w-4 h-4 mr-2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Späť na zoznam
            </button>
          </div>

          <!-- Center section with title -->
          <div class="flex-1 text-center sm:mx-6">
            <div class="flex items-center justify-center space-x-3">
              <div class="p-2 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                  stroke="currentColor" class="w-6 h-6 text-white">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              </div>
              <div>
                <h1 class="text-2xl sm:text-3xl font-bold text-white">
                  {{ currentPost.id ? `Článok #${currentPost.id}` : 'Detail článku' }}
                </h1>
                <p v-if="currentPost.title" class="text-teal-100 text-sm sm:text-base mt-1 font-medium">
                  {{ currentPost.title }}
                </p>
              </div>
            </div>
          </div>

          <!-- Right section with actions -->
          <div class="flex items-center justify-center sm:justify-end space-x-3">
            <button v-if="currentPost.id" @click="isDeleteDialogOpen = true" type="button"
              class="inline-flex items-center px-4 py-2 border border-red-300 border-opacity-30 rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 bg-opacity-90 hover:bg-opacity-100 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-opacity-50 backdrop-blur-sm transition-all duration-200">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                stroke="currentColor" class="w-4 h-4 mr-2">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Zmazať
            </button>
          </div>
        </div>
      </div>
    </div> <!-- Enhanced Loading state with shimmer effect -->
    <div v-if="loading" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div class="animate-pulse">
          <!-- Header skeleton -->
          <div class="flex items-center justify-center mb-8">
            <div class="w-10 h-10 bg-gradient-to-r from-teal-200 to-cyan-200 rounded-full mr-4 shimmer"></div>
            <div class="space-y-2">
              <div class="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-48 shimmer"></div>
              <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32 shimmer"></div>
            </div>
          </div>

          <!-- Content skeleton -->
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <!-- Sidebar skeleton -->
            <div class="space-y-6">
              <div class="bg-gray-50 rounded-lg p-4 space-y-4">
                <div class="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4 shimmer"></div>
                <div class="space-y-3">
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full shimmer"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-2/3 shimmer"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-4/5 shimmer"></div>
                </div>
              </div>
              <div class="h-32 bg-gradient-to-r from-gray-200 to-gray-300 rounded shimmer"></div>
            </div>

            <!-- Main content skeleton -->
            <div class="lg:col-span-2 space-y-6">
              <div class="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-1/4 shimmer"></div>
              <div class="space-y-4">
                <div class="h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded shimmer"></div>
                <div class="grid grid-cols-2 gap-4">
                  <div class="h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded shimmer"></div>
                  <div class="h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded shimmer"></div>
                </div>
                <div class="h-64 bg-gradient-to-r from-gray-200 to-gray-300 rounded shimmer"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Error state -->
    <div v-else-if="error" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden">
        <div class="bg-red-50 px-6 py-4 border-b border-red-200">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg class="w-5 h-5 text-red-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                  fill="currentColor">
                  <path fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-red-800">Chyba pri načítavaní článku</h3>
              <p class="text-sm text-red-600 mt-1">{{ error }}</p>
            </div>
          </div>
        </div>
        <div class="px-6 py-4">
          <button @click="fetchPost(route.params.id)"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
              stroke="currentColor" class="w-4 h-4 mr-2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            Skúsiť znovu
          </button>
        </div>
      </div>
    </div> <!-- Enhanced Delete confirmation dialog -->
    <div v-show="isDeleteDialogOpen"
      class="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div class="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-auto transform transition-all">
        <div class="bg-red-50 px-6 pt-6 pb-4 rounded-t-xl">
          <div class="flex items-center">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
          </div>
        </div>
        <div class="px-6 py-4">
          <h3 class="text-lg font-semibold text-gray-900 text-center mb-2">Zmazanie článku</h3>
          <p class="text-sm text-gray-600 text-center mb-6">
            Naozaj chcete zmazať tento článok? Táto akcia je nenávratná a článok bude natrvalo odstránený z databázy.
          </p>
          <div v-if="currentPost.title" class="bg-gray-50 rounded-lg p-3 mb-6">
            <p class="text-sm font-medium text-gray-900">{{ currentPost.title }}</p>
            <p class="text-xs text-gray-500 mt-1">ID: {{ currentPost.id }}</p>
          </div>
          <div class="flex flex-col sm:flex-row gap-3">
            <button type="button" @click="isDeleteDialogOpen = false"
              class="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200">
              Zrušiť
            </button>
            <button type="button" @click="deletePost()"
              class="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                stroke="currentColor" class="w-4 h-4 mr-2">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Zmazať článok
            </button>
          </div>
        </div>
      </div>
    </div><!-- Main content -->
    <div v-if="!loading && !error" class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="px-4 py-6 sm:px-0">
        <div class="mt-10 sm:mt-0">
          <div class="md:grid md:grid-cols-3 md:gap-6"> <!-- Sidebar with categories and metadata -->
            <div class="md:col-span-1">
              <div class="px-4 sm:px-0 space-y-6">
                <!-- Enhanced Metadata Card -->
                <div v-if="currentPost.id" class="transform transition-all duration-300 hover:scale-105">
                  <div
                    class="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                    <div class="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4">
                      <h3 class="text-lg font-semibold text-white flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                          stroke="currentColor" class="w-5 h-5 mr-2">
                          <path stroke-linecap="round" stroke-linejoin="round"
                            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                        </svg>
                        Informácie o článku
                      </h3>
                    </div>
                    <div class="px-6 py-5">
                      <dl class="space-y-4">
                        <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <dt class="text-sm font-medium text-gray-600">ID článku</dt>
                          <dd class="text-sm font-semibold text-gray-900 bg-white px-2 py-1 rounded">#{{ currentPost.id
                          }}</dd>
                        </div>
                        <div v-if="selectedCategory"
                          class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <dt class="text-sm font-medium text-gray-600">Kategória</dt>
                          <dd>
                            <span
                              class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                              <span class="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                              {{ selectedCategory.name }}
                            </span>
                          </dd>
                        </div>
                        <div v-if="formattedCreatedDate" class="flex flex-col p-3 bg-gray-50 rounded-lg">
                          <dt class="text-sm font-medium text-gray-600 mb-1">Vytvorené</dt>
                          <dd class="text-sm text-gray-900 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                              stroke="currentColor" class="w-4 h-4 mr-2 text-green-500">
                              <path stroke-linecap="round" stroke-linejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {{ formattedCreatedDate }}
                          </dd>
                        </div>
                        <div v-if="formattedUpdatedDate" class="flex flex-col p-3 bg-gray-50 rounded-lg">
                          <dt class="text-sm font-medium text-gray-600 mb-1">Posledná úprava</dt>
                          <dd class="text-sm text-gray-900 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                              stroke="currentColor" class="w-4 h-4 mr-2 text-amber-500">
                              <path stroke-linecap="round" stroke-linejoin="round"
                                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                            </svg>
                            {{ formattedUpdatedDate }}
                          </dd>
                        </div>

                        <!-- Image Preview in Sidebar -->
                        <div v-if="displayImageUrl" class="flex flex-col p-3 bg-gray-50 rounded-lg">
                          <dt class="text-sm font-medium text-gray-600 mb-2">Aktuálny obrázok</dt>
                          <dd> <img :src="displayImageUrl" :alt="currentPost.title || 'Obrázok článku'"
                              class="w-full max-w-xs h-auto rounded-lg border border-gray-200 shadow-sm image-preview"
                              @error="handleImageError" />
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>

                <!-- Enhanced Categories Table -->
                <div class="transform transition-all duration-300 hover:scale-105">
                  <div class="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                    <div class="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-4">
                      <h3 class="text-lg font-semibold text-white flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                          stroke="currentColor" class="w-5 h-5 mr-2">
                          <path stroke-linecap="round" stroke-linejoin="round"
                            d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                        </svg>
                        Dostupné kategórie
                      </h3>
                    </div>
                    <div class="overflow-hidden">
                      <div v-if="postCategories.length === 0" class="px-6 py-8 text-center">
                        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24"
                          stroke="currentColor" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 class="mt-2 text-sm font-medium text-gray-900">Žiadne kategórie</h3>
                        <p class="mt-1 text-sm text-gray-500">Zatiaľ neboli vytvorené žiadne kategórie.</p>
                      </div>
                      <div v-else class="max-h-80 overflow-y-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                          <thead class="bg-gray-50 sticky top-0">
                            <tr>
                              <th scope="col"
                                class="px-4 py-3 text-xs font-bold text-left text-gray-500 uppercase tracking-wider">
                                ID
                              </th>
                              <th scope="col"
                                class="px-4 py-3 text-xs font-bold text-left text-gray-500 uppercase tracking-wider">
                                Názov
                              </th>
                              <th scope="col"
                                class="px-4 py-3 text-xs font-bold text-left text-gray-500 uppercase tracking-wider">
                                Popis
                              </th>
                            </tr>
                          </thead>
                          <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="postCategory in postCategories" :key="postCategory.id"
                              :class="{ 'bg-blue-50 border-l-4 border-blue-400': currentPost.category_id === postCategory.id }"
                              class="hover:bg-gray-50 transition-colors duration-150">
                              <td class="px-4 py-3 text-sm font-medium text-gray-800">
                                <span
                                  class="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-xs font-bold">
                                  {{ postCategory.id }}
                                </span>
                              </td>
                              <td class="px-4 py-3 text-sm font-medium text-gray-900">
                                {{ postCategory.name }}
                              </td>
                              <td class="px-4 py-3 text-sm text-gray-600 max-w-xs truncate"
                                :title="postCategory.description">
                                {{ postCategory.description || 'Bez popisu' }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div> <!-- Enhanced Main form -->
            <div class="mt-5 md:mt-0 md:col-span-2">
              <div class="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <!-- Form Header -->
                <div class="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
                  <h3 class="text-lg font-semibold text-white flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                      stroke="currentColor" class="w-5 h-5 mr-2">
                      <path stroke-linecap="round" stroke-linejoin="round"
                        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                    </svg>
                    Úprava článku
                  </h3>
                </div>

                <form @submit.prevent="updatePost">
                  <div class="px-6 py-6">
                    <div class="space-y-6">
                      <!-- Title -->
                      <div class="group">
                        <label for="title"
                          class="block text-sm font-medium text-gray-700 mb-2 transition-colors group-focus-within:text-teal-600">
                          <span class="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                              stroke="currentColor" class="w-4 h-4 mr-2">
                              <path stroke-linecap="round" stroke-linejoin="round"
                                d="M7.5 8.25h9m-9 3H12m-9.75 1.05c0 1.06.85 1.92 1.9 1.92h1.25l2.25 2.25L8.25 18l.9-.9H12a3 3 0 003-3V8.25c0-1.66-1.35-3-3-3H4.5c-1.65 0-3 1.34-3 3v7.55z" />
                            </svg>
                            Názov článku *
                          </span>
                        </label>
                        <FormKit type="text" v-model="currentPost.title" name="title" id="title"
                          placeholder="Zadajte výstižny názov článku..." validation="required"
                          validation-visibility="live" :classes="{
                            outer: 'formkit-disabled:opacity-50',
                            wrapper: 'relative',
                            inner: 'formkit-has-errors:border-red-300 focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-teal-500 transition-all duration-200',
                            input: 'block w-full px-4 py-3 border-gray-300 rounded-lg shadow-sm text-sm placeholder-gray-400 focus:outline-none'
                          }" />
                      </div>

                      <!-- Slug Field -->
                      <div class="group">
                        <label for="slug"
                          class="block text-sm font-medium text-gray-700 mb-2 transition-colors group-focus-within:text-teal-600">
                          <span class="flex items-center justify-between">
                            <span class="flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="currentColor" class="w-4 h-4 mr-2">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                  d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                              </svg>
                              URL Slug
                            </span>
                            <span class="text-xs text-gray-500 font-normal">
                              (automaticky vygenerované)
                            </span>
                          </span>
                        </label>
                        <div class="relative">
                          <FormKit type="text" v-model="currentPost.slug" name="slug" id="slug"
                            placeholder="url-slug-clanku" :classes="{
                              outer: 'formkit-disabled:opacity-50',
                              wrapper: 'relative',
                              inner: 'focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-teal-500 transition-all duration-200',
                              input: 'block w-full px-4 py-3 border-gray-300 rounded-lg shadow-sm text-sm placeholder-gray-400 focus:outline-none font-mono'
                            }" />
                        </div>
                        <p class="text-xs text-gray-500 mt-2">
                          URL: https://bizinix.sk/post/{{ currentPost.slug || 'url-slug' }}
                        </p>
                      </div>

                      <!-- Category and Image Row -->
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Category Selection -->
                        <div class="group">
                          <label for="category_id"
                            class="block text-sm font-medium text-gray-700 mb-2 transition-colors group-focus-within:text-teal-600">
                            <span class="flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="currentColor" class="w-4 h-4 mr-2">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                  d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6A1.125 1.125 0 012.25 10.875v-3.75zM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-8.25zM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25A1.125 1.125 0 012.25 18.375v-2.25z" />
                              </svg>
                              Kategória
                            </span>
                          </label>
                          <select v-model="currentPost.category_id" name="category_id" id="category_id"
                            class="block w-full px-4 py-3 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm transition-all duration-200 hover:border-gray-400">
                            <option value="">Vyberte kategóriu...</option>
                            <option v-for="category in postCategories" :key="category.id" :value="category.id">
                              {{ category.name }}
                            </option>
                          </select>
                        </div> <!-- Image Upload with Preview -->
                        <div class="group">
                          <label for="image"
                            class="block text-sm font-medium text-gray-700 mb-2 transition-colors group-focus-within:text-teal-600">
                            <span class="flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="currentColor" class="w-4 h-4 mr-2">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                  d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                              </svg>
                              Obrázok
                              <span v-if="currentPost.image" class="text-teal-600 font-medium ml-2">
                                ({{ currentPost.image }})
                              </span>
                            </span>
                          </label>
                          <!-- Image Preview -->
                          <div v-if="displayImageUrl" class="mb-3">
                            <div class="relative inline-block"> <img :src="displayImageUrl"
                                :alt="currentPost.title || 'Náhľad obrázka'"
                                class="w-32 h-32 object-cover rounded-lg border border-gray-200 shadow-sm image-preview"
                                @error="handleImageError" />
                              <button @click="removeImage" type="button"
                                class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors duration-200 remove-image-btn"
                                title="Odstrániť obrázok">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                                  stroke="currentColor" class="w-3 h-3">
                                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                            <p class="text-xs text-gray-500 mt-1">
                              {{ imagePreviewUrl ? 'Nový obrázok (pred uložením)' : 'Aktuálny obrázok' }}
                            </p>
                          </div>

                          <!-- File Input -->
                          <FormKit :classes="{
                            outer: 'formkit-disabled:opacity-50',
                            wrapper: 'relative',
                            inner: 'focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-teal-500 transition-all duration-200 hover:border-gray-400',
                            input: 'block w-full px-4 py-3 border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100'
                          }" id="image" accept="image/*" @change="updateImage" name="image" type="file" />

                          <!-- Upload Instructions -->
                          <p class="text-xs text-gray-500 mt-2">
                            Podporované formáty: JPG, PNG, GIF. Maximálna veľkosť: 5MB
                          </p>
                        </div>
                      </div>

                      <!-- Content Editor -->
                      <div class="group">
                        <label
                          class="block text-sm font-medium text-gray-700 mb-2 transition-colors group-focus-within:text-teal-600">
                          <span class="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                              stroke="currentColor" class="w-4 h-4 mr-2">
                              <path stroke-linecap="round" stroke-linejoin="round"
                                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                            </svg>
                            Obsah článku *
                          </span>
                        </label>
                        <div
                          class="border-2 border-gray-200 rounded-lg focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500 focus-within:ring-opacity-20 transition-all duration-200">
                          <RichTextEditor v-model="currentPost.content" id="content" :height="400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Enhanced Form Footer -->
                  <div
                    class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <!-- Progress indicator (if updating) -->
                    <div v-if="isUpdating" class="flex items-center text-sm text-gray-600">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-teal-600" xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                        </path>
                      </svg>
                      Ukladám zmeny...
                    </div>
                    <!-- <div v-else class="flex items-center text-sm text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="w-4 h-4 mr-2">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                      </svg>
                      Všetky zmeny sa uložia automaticky
                    </div> -->

                    <!-- Action buttons -->
                    <div class="flex flex-col sm:flex-row gap-3">
                      <button type="button" @click="goBack"
                        class="inline-flex justify-center items-center px-6 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200">
                        Zrušiť
                      </button> <button type="submit" :disabled="isUpdating || !isFormValid" :class="[
                        'inline-flex justify-center items-center px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white transition-all duration-200 hover:scale-105',
                        isFormValid && !isUpdating
                          ? 'bg-teal-600 hover:bg-teal-700 focus:ring-teal-500'
                          : 'bg-gray-400 cursor-not-allowed'
                      ]">
                        <svg v-if="isUpdating" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                          </circle>
                          <path class="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                          </path>
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                          stroke="currentColor" class="w-4 h-4 mr-2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        {{ isUpdating ? 'Ukladám...' : 'Uložiť zmeny' }}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Leave warning dialog -->
    <div v-show="isLeaveWarningOpen"
      class="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div class="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-auto transform transition-all">
        <div class="bg-amber-50 px-6 pt-6 pb-4 rounded-t-xl">
          <div class="flex items-center">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                stroke="currentColor" class="w-6 h-6 text-amber-600">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
          </div>
        </div>
        <div class="px-6 py-4">
          <h3 class="text-lg font-semibold text-gray-900 text-center mb-2">Neuložené zmeny</h3>
          <p class="text-sm text-gray-600 text-center mb-6">
            Máte neuložené zmeny. Ak opustíte túto stránku, všetky zmeny budú stratené.
          </p>
          <div class="flex flex-col sm:flex-row gap-3">
            <button type="button" @click="cancelLeave"
              class="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200">
              Zostať na stránke
            </button>
            <button type="button" @click="confirmLeave"
              class="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-200">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                stroke="currentColor" class="w-4 h-4 mr-2">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
              Opustiť stránku
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Unsaved changes warning dialog -->
  <div v-if="isLeaveWarningOpen"
    class="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
    <div class="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-auto transform transition-all">
      <div class="bg-yellow-50 px-6 pt-6 pb-4 rounded-t-xl">
        <div class="flex items-center">
          <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <svg class="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
        </div>
      </div>
      <div class="px-6 py-4">
        <h3 class="text-lg font-semibold text-gray-900 text-center mb-2">Nezabudnite uložiť zmeny</h3>
        <p class="text-sm text-gray-600 text-center mb-4">
          Máte neuložené zmeny. Naozaj chcete opustiť túto stránku?
        </p>
        <div class="flex flex-col sm:flex-row gap-3">
          <button type="button" @click="cancelLeave"
            class="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200">
            Zostať na stránke
          </button>
          <button type="button" @click="confirmLeave"
            class="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-200">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
              stroke="currentColor" class="w-4 h-4 mr-2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
            </svg>
            Opustiť stránku
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Shimmer effect for loading states */
@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }

  100% {
    background-position: 1000px 0;
  }
}

.shimmer {
  background: linear-gradient(90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(255, 255, 255, 0) 100%);
  background-size: 1000px 100%;
  animation: shimmer 2s infinite linear;
}

/* Smooth transitions for hover effects */
.group:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}

/* Focus ring improvements */
.focus\:ring-teal-500:focus {
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
}

/* Custom scrollbar for category table */
.max-h-80::-webkit-scrollbar {
  width: 6px;
}

.max-h-80::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.max-h-80::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.max-h-80::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Enhanced form field focus styles */
.formkit-outer .formkit-inner:focus-within {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.15);
}

/* Button hover animations */
.hover\:scale-105:hover {
  transform: scale(1.05);
}

/* Backdrop blur for modern glass effect */
.backdrop-blur-sm {
  backdrop-filter: blur(4px);
}

/* Image preview styles */
.image-preview {
  transition: transform 0.2s ease;
}

.image-preview:hover {
  transform: scale(1.02);
}

/* Remove button for image preview */
.remove-image-btn {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.remove-image-btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}
</style>